import React, {useEffect, useState} from 'react';
import { Form, Button, Nav, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { iCloudService } from '../services/iCloudService.js';
import {useEnvironment} from "@/contexts/EnvironmentContext.jsx";
import _ from "lodash";
import {useAuth} from "@/contexts/AuthContext.jsx";

const ICloudIntegration = ({ userIcloudCredentials }) => {
    const { user } = useAuth();
    const {
        environment,        // 'development' or 'production'
        isDevelopment,      // boolean flag
        isProduction,       // boolean flag
        calendarApiEndpoints,       // object with API endpoints
        changeEnvironment   // function to change environment
    } = useEnvironment();
    const [iCloudEmail, setiCloudEmail] = useState('');
    const [iCloudPassword, setiCloudPassword] = useState('');
    const [iCloudLoading, setiCloudLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const [showIcloudLogin, setShowIcloudLogin] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [calendarList, setCalendarList] = useState([]);
    const [selectedCalendars, setSelectedCalendars] = useState([]);
    const [syncResponse, setSyncResponse] = useState(null);
    const [autoConnectAttempted, setAutoConnectAttempted] = useState(false);

    // Auto-connect when userIcloudCredentials are provided
    useEffect(() => {
        if (userIcloudCredentials && !autoConnectAttempted && calendarApiEndpoints.icloud) {
            handleAutoConnect();
        }
    }, [userIcloudCredentials, autoConnectAttempted, calendarApiEndpoints.icloud]);

    useEffect(() => {
    }, [calendarList]);

    const handleAutoConnect = async () => {
        if (!userIcloudCredentials || !userIcloudCredentials.username || !userIcloudCredentials.password) {
            console.warn('Invalid userIcloudCredentials provided');
            return;
        }

        setAutoConnectAttempted(true);
        setiCloudLoading(true);
        setSyncResponse(null);

        // Set the credentials in state
        setiCloudEmail(userIcloudCredentials.username);
        setiCloudPassword(userIcloudCredentials.password);

        try {
            const response = await iCloudService.connectToICloud(
                userIcloudCredentials.username,
                userIcloudCredentials.password,
                calendarApiEndpoints.icloud,
                false
            );

            setCalendarList(response.calendars);
            setIsConnected(true);

            // Pre-select previously saved calendars (like Google does)
            if (userIcloudCredentials.calendars && userIcloudCredentials.calendars.length > 0) {
                const savedCalendarIds = userIcloudCredentials.calendars.map(
                    cal => cal.calendar_id || cal.calendarId || cal.id
                );
                setSelectedCalendars(savedCalendarIds);
            }

        } catch (error) {
            console.error('Error auto-connecting to iCloud:', error);
            setSyncResponse({ error: error.message || 'Failed to auto-connect to iCloud' });
            setIsConnected(false);
        } finally {
            setiCloudLoading(false);
        }
    };

    const handleSubmitIcloudLogin = async (e) => {
        e.preventDefault();
        setiCloudLoading(true);
        setSyncResponse(null); // Clear any previous sync response

        try {
            console.log('--handleSubmitIcloudLogin--->', {
                email: iCloudEmail,
                password: iCloudPassword
            });

            const response = await iCloudService.connectToICloud(iCloudEmail, iCloudPassword, calendarApiEndpoints.icloud);

            setCalendarList(response.calendars);
            setIsConnected(true);

        } catch (error) {
            console.error('Error connecting to iCloud:', error);
            setSyncResponse({ error: error.message || 'Failed to connect to iCloud' });
        } finally {
            setiCloudLoading(false);
        }
    };

    const handleCalendarSelection = (calendarId) => {
        setSelectedCalendars(prev => {
            if (prev.includes(calendarId)) {
                return prev.filter(id => id !== calendarId);
            } else {
                return [...prev, calendarId];
            }
        });
    };

    const handleSync = async (e) => {
        e.preventDefault();
        setSyncLoading(true);
        setSyncResponse(null); // Clear previous response

        try {
            const calendarsToSync = selectedCalendars.map(calendarId => {
                const calendar = calendarList.find(cal => cal.id === calendarId);
                return calendar;
            }).filter(Boolean);

            const today = new Date();
            const sixtyDaysLater = new Date(today);
            sixtyDaysLater.setDate(today.getDate() + 60);

            const response = await iCloudService.syncICloud(user.uid, calendarApiEndpoints.icloud,
                iCloudEmail, iCloudPassword, false , today, sixtyDaysLater, calendarsToSync, true);

            setSyncResponse(response);
            console.log('[handleSync] Response:', response);
        } catch (error) {
            console.error('Error syncing iCloud:', error);
            setSyncResponse({ error: error.message || 'An error occurred during sync' });
        } finally {
            setSyncLoading(false);
        }
    }

    const handleCancel = () => {
        setShowIcloudLogin(false);
        setSyncResponse(null);
    };

    const handleDisconnect = async (e) => {
        e.preventDefault();

        const confirmDisconnect = window.confirm(
            'Are you sure you want to disconnect and reset your iCloud integration? This will NOT remove all synced calendar data.'
        );

        // If user cancels, exit early
        if (!confirmDisconnect) {
            return;
        }

        setResetLoading(true);
        setSyncResponse(null);

        try {
            const response = await iCloudService.resetICloud(user.uid, calendarApiEndpoints.icloud);
            console.log('[handleDisconnect] Response:', response);

            // Reset state
            setIsConnected(false);
            setiCloudEmail('');
            setiCloudPassword('');
            setCalendarList([]);
            setSelectedCalendars([]);
            setAutoConnectAttempted(false);
            setShowIcloudLogin(false);
            setSyncResponse({ success: true, message: 'iCloud Calendar disconnected successfully' });
        } catch (error) {
            console.error('Error disconnecting iCloud:', error);
            setSyncResponse({error: error.message || 'An error occurred during disconnect'});
        } finally {
            setResetLoading(false);
        }
    };

    // iCloud icon component
    const ICloudIcon = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="#007AFF"/>
        </svg>
    );

    return (
        <div className="integration-item">
            {/* Loading indicator for connection status check */}
            {iCloudLoading && !showIcloudLogin && (
                <div className="d-flex align-items-center gap-2 mb-3 p-3 bg-light rounded">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted">Checking iCloud Calendar connection...</span>
                </div>
            )}

            {/* Disconnected, collapsed state - show "iCloud Calendar" with "+" icon */}
            {!showIcloudLogin && !isConnected && !iCloudLoading && (
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <Nav.Link
                            onClick={() => setShowIcloudLogin(true)}
                            className="p-1"
                            style={{cursor: 'pointer'}}
                        >
                            <Form.Label className="mb-0">iCloud Calendar</Form.Label>
                            <FontAwesomeIcon className="mb-0 ms-2" icon={faPlus}/>
                        </Nav.Link>
                    </div>
                </div>
            )}

            {/* Connected, collapsed state - show checkmark, email, and "Manage" button */}
            {!showIcloudLogin && isConnected && (
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                        <Form.Label className="mb-0">
                            iCloud Calendar Connected
                            {iCloudEmail && <span className="text-muted ms-2">({iCloudEmail})</span>}
                        </Form.Label>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowIcloudLogin(true)}
                            style={{ borderRadius: '56px' }}
                        >
                            Manage
                        </Button>
                    </div>
                </div>
            )}

            {/* Expanded state */}
            {showIcloudLogin && (
                <div>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <ICloudIcon />
                        <h6 className="mb-0">iCloud Calendar</h6>
                        {iCloudLoading && <Spinner size="sm" />}
                    </div>

                    {syncResponse?.error && (
                        <Alert variant="danger" className="mb-3">
                            {syncResponse.error}
                        </Alert>
                    )}

                    {!isConnected ? (
                        <>
                            <p className="text-muted small mb-3">
                                Connect your iCloud Calendar to sync events automatically.
                                You'll need an app-specific password from Apple.
                                {' '}
                                <Nav.Link
                                    as="a"
                                    target="_blank"
                                    href="https://support.apple.com/en-us/102654"
                                    className="small text-decoration-none d-inline p-0"
                                >
                                    (Create an iCloud App Password)
                                </Nav.Link>
                            </p>

                            <Form onSubmit={handleSubmitIcloudLogin}>
                                <Form.Group id="icloud-email" className="mb-3">
                                    <Form.Label>iCloud Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={iCloudEmail}
                                        onChange={(e) => setiCloudEmail(e.target.value)}
                                        placeholder="your.email@domain.com"
                                        required
                                    />
                                </Form.Group>

                                <Form.Group id="icloud-password" className="mb-3">
                                    <Form.Label>iCloud App Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={iCloudPassword}
                                        onChange={(e) => setiCloudPassword(e.target.value)}
                                        placeholder="Enter your app-specific password"
                                        required
                                    />
                                </Form.Group>

                                <div className="d-flex gap-2 mb-3">
                                    <Button
                                        disabled={iCloudLoading}
                                        type="submit"
                                        size="sm"
                                        variant="primary"
                                        style={{ borderRadius: '56px', minWidth: '120px' }}
                                    >
                                        {iCloudLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Connecting...
                                            </>
                                        ) : (
                                            <>
                                                <ICloudIcon />
                                                <span className="ms-2">Connect with iCloud</span>
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        type="button"
                                        onClick={handleCancel}
                                        style={{ borderRadius: '56px', minWidth: '80px' }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Form>
                        </>
                    ) : (
                        <>
                            {calendarList.length > 0 && (
                                <div className="mb-3">
                                    <Form.Label>Select calendars to get items for the next 60 days:</Form.Label>
                                    {calendarList.map(calendar => (
                                        <Form.Check
                                            key={calendar.id}
                                            type="checkbox"
                                            id={`icloud-calendar-${calendar.id}`}
                                            label={calendar.label || calendar.name || calendar.category_id}
                                            checked={selectedCalendars.includes(calendar.id)}
                                            onChange={() => handleCalendarSelection(calendar.id)}
                                        />
                                    ))}
                                </div>
                            )}

                            <div className="d-flex gap-2 mb-3">
                                {selectedCalendars.length > 0 && (
                                    <Button
                                        disabled={syncLoading}
                                        type="button"
                                        size="sm"
                                        variant="primary"
                                        style={{ borderRadius: '56px', minWidth: '80px' }}
                                        onClick={handleSync}
                                    >
                                        {syncLoading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                Syncing...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSync} className="me-2" />
                                                Select Calendars to Sync
                                            </>
                                        )}
                                    </Button>
                                )}

                                <Button
                                    disabled={resetLoading}
                                    onClick={handleDisconnect}
                                    size="sm"
                                    variant="outline-danger"
                                    style={{ borderRadius: '56px', minWidth: '100px' }}
                                >
                                    {resetLoading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Disconnecting...
                                        </>
                                    ) : (
                                        'Disconnect'
                                    )}
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    type="button"
                                    onClick={handleCancel}
                                    style={{ borderRadius: '56px', minWidth: '80px' }}
                                >
                                    Close
                                </Button>
                            </div>
                        </>
                    )}

                    {/* Display sync response */}
                    {syncResponse && !syncResponse.error && (
                        <div className="mt-3">
                            <Alert variant="success">
                                <strong>Success!</strong>{' '}
                                {syncResponse.message || 'Calendar synced successfully'}
                            </Alert>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ICloudIntegration;
